import { Amplify as AWSAmplify, ResourcesConfig } from 'ALIASED-aws-amplify';
import { Hub as AWSHub, sessionStorage } from 'ALIASED-aws-amplify/utils';
import ky, { KyResponse, Options, Input } from 'ky';
import { getRuntimeConfig } from '../src/hooks/Runtime';
import { cognitoUserPoolsTokenProvider } from 'ALIASED-aws-amplify/auth/cognito';
import { type SignInOutput } from 'ALIASED-aws-amplify/auth';
import 'ALIASED-aws-amplify/auth/enable-oauth-listener';

import {
  signIn,
  signOut,
  fetchAuthSession,
  signInWithRedirect,
  confirmSignIn
} from 'ALIASED-aws-amplify/auth';

export type SignInStep = SignInOutput['nextStep']['signInStep'];

export type CognitoUser = {
  username: string;
  challengeName: SignInStep;
  additionalInfoRequiredField?: string;
};

export type { ResourcesConfig };

const apiClient = ky.extend({
  timeout: false,
  retry: {
    statusCodes: [429],
    limit: 3
  },
  hooks: {
    beforeError: [
      // to work smoothly with useMutation, ensure that BE error messages are included in error.message
      async (error) => {
        const { response } = error;

        if (response?.body && response.headers.get('Content-Type') === 'application/json') {
          const { message } = await response.clone().json();
          error.message = `${message} (status ${response.status})`;
        }

        return error;
      }
    ],
    beforeRequest: [
      async (request) => {
        if (request.method === 'GET') {
          request.headers.set('X-Api-Key', window.location.toString());
        }
      }
    ]
  }
});

const noRetryApiClient = apiClient.extend({
  retry: {
    statusCodes: [],
    limit: 0
  }
});

// List of endpoints that should not retry on 429 errors
const NO_RETRY_ENDPOINTS = ['/resend-welcome'];

function chooseClient(path?: string) {
  // Check if this endpoint should skip retry
  if (path && NO_RETRY_ENDPOINTS.some((endpoint) => path.includes(endpoint))) {
    return noRetryApiClient;
  }

  return getRuntimeConfig().DISABLED_EXPONENTIAL_BACKOFF ? noRetryApiClient : apiClient;
}

async function unpackResponse(r: Response) {
  if (r.status === 204) {
    return '';
  }

  const text = await r.text();

  if (!text) return [];

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function makeRequest(
  endpoint: string,
  path: string,
  init: Record<string, any>,
  callback: (url: Input, options?: Options) => Promise<KyResponse>
) {
  if (!Auth.isInHARMode) {
    init.headers = init.headers || {};

    init.headers['X-Api-Key'] = window.location.toString();

    if (!init.noAuth) {
      const currentSession = await Auth.currentSession();
      init.headers.Authorization = `Bearer ${currentSession.getIdToken().getJwtToken()}`;
    }
  }

  // translate to a fetch call in order to hit service worker in HAR mode
  const qs = Object.fromEntries(
    Object.entries(init.queryStringParameters || {}).map(([key, value]) => [key, value ?? ''] as [string, string])
  );

  const sp = new URLSearchParams(qs).toString();
  const { API_URL } = getRuntimeConfig();
  const res = await callback(`${API_URL}${path}` + (sp ? `?${sp}` : ''), {
    body: init.body ? JSON.stringify(init.body) : null,
    headers: init.headers
  }).catch(async (e) => {
    if (e.response) {
      const res = e.response;

      res.data = await unpackResponse(res.clone()).catch();
    }
    throw e;
  });

  if (!res.ok) {
    const e = new Error(`Request failed with status ${res.status}`) as any;

    e.response = {
      status: res.status,
      statusText: res.statusText,
      data: await res.json().catch()
    };

    throw e;
  }
  const data = await unpackResponse(res);
  return { data, status: res.status, statusText: res.statusText };
}

export const API: {
  get: (endpoint: string, path: string, init?: {}) => ReturnType<typeof makeRequest>;
  post: (endpoint: string, path: string, init?: {}) => ReturnType<typeof makeRequest>;
  put: (endpoint: string, path: string, init?: {}) => ReturnType<typeof makeRequest>;
} = {
  get: async (endpoint: string, path: string, init = {}) => {
    return makeRequest(endpoint, path, init, chooseClient(path));
  },
  post: async (endpoint: string, path: string, init = {}) => {
    return makeRequest(endpoint, path, init, chooseClient(path).post);
  },
  put: async (endpoint: string, path: string, init = {}) => {
    return makeRequest(endpoint, path, init, chooseClient(path).put);
  }
};

export const Amplify: {
  Logger: { LOG_LEVEL: number };
  configure: typeof AWSAmplify.configure;
} = {
  Logger: { LOG_LEVEL: 0 },
  configure: (...config) => {
    if (!Auth.isInHARMode) {
      AWSAmplify.configure(...config);
      cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
    }

    return {};
  }
};

export const Hub: Pick<typeof AWSHub, 'listen'> = {
  listen: AWSHub.listen.bind(AWSHub)
};

export { AWSHub };

(function initializeStorageBridge() {
  if (typeof window === 'undefined') return;

  const originalSetItem = window.localStorage.setItem;

  window.localStorage.setItem = function (key, value) {
    // Catch when v6 writes the PKCE verifier and mirror it for v4
    if (key.endsWith('.oauthPKCE')) {
      window.sessionStorage.setItem('ouath_pkce_key', value); // v4 typo included
    }

    // Catch when v6 writes the OAuth state and mirror it for v4
    if (key.endsWith('oauthState')) {
      window.sessionStorage.setItem('oauth_state', value);

      // Explicitly seed the legacy Hosted UI flags v4 relies on
      window.sessionStorage.setItem('amplify-signin-with-hostedUI', 'true');
      window.sessionStorage.setItem('amplify-redirected-from-hosted-ui', 'true');
    }

    originalSetItem.apply(this, [key, value]);
  };
})();

// @ts-ignore
export const Auth = {
  /** GUID of the user simulated by a HAR replay. Empty outside HAR mode — not the real logged-in user. */
  harUserId: '',

  /** True while replaying a HAR file. Use this (not harUserId) to check for HAR mode. */
  isInHARMode: false,

  mockedAuth: true,

  getCurrentUser: async () => {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      const payload = session.tokens.idToken.payload;
      return {
        username: payload['cognito:username'] || payload.preferred_username || payload.email || payload.sub,
        userId: payload.sub
      };
    }
  },

  currentAuthenticatedUser: async () => {
    if (!Auth.isInHARMode) {
      return Auth.getCurrentUser();
    }
    return Promise.resolve({ username: 'dummy-har@mailinator.com' });
  },

  signOut: (...args: Parameters<typeof signOut>) => {
    if (!Auth.isInHARMode) return signOut(...args);
    return Promise.resolve({});
  },

  signIn: async (email: string, password?: string, options?: Parameters<typeof signIn>[0]['options']) => {
    if (!Auth.isInHARMode) {
      return signIn({ username: email, password, options }).then((r) => ({
        username: email,
        challengeName: r.nextStep.signInStep
      }));
    }
    return Promise.resolve({ username: email, challengeName: 'DONE' as SignInStep });
  },

  federatedSignIn: async ({
    provider,
    customState = btoa(
      JSON.stringify({
        storyPath: window.location.pathname
      })
    )
  }: {
    provider: string;
    customState?: string;
  }) => {
    // Notice the subtleties about the localStorage interceptor above
    if (!Auth.isInHARMode) return signInWithRedirect({ provider: { custom: provider }, customState });
    return Promise.resolve({});
  },

  sendCustomChallengeAnswer: async (cog: CognitoUser, challengeResponse: string): Promise<CognitoUser> => {
    if (!Auth.isInHARMode) {
      return confirmSignIn({ challengeResponse }).then(({ nextStep }) => {
        if (nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' && nextStep.signInStep !== 'DONE') {
          throw Error('Expected CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE during restricted login');
        }

        return {
          username: cog.username,
          challengeName: nextStep.signInStep,
          additionalInfoRequiredField:
            nextStep.signInStep !== 'DONE' ? nextStep.additionalInfo?.requiredField : undefined
        };
      });
    }

    return Promise.resolve({
      username: cog.username,
      challengeName: 'DONE' as SignInStep,
      additionalInfoRequiredField: undefined
    });
  },

  fetchAuthSession: (...args: Parameters<typeof fetchAuthSession>) => fetchAuthSession(...args),

  currentSession: async (...args: Parameters<typeof fetchAuthSession>) => {
    if (!Auth.isInHARMode) {
      const { tokens } = await fetchAuthSession(...args);
      if (!tokens) {
        return Promise.reject('No current user');
      }
      return {
        accessToken: tokens.accessToken,
        getIdToken: () => ({
          ...tokens.idToken,
          getJwtToken: () => tokens.idToken?.toString() || '',
          getExpiration: () => {
            const exp = tokens.idToken?.payload.exp || 0;

            if (!exp) throw Error('Missing expiration in idToken');

            return exp * 1000;
          }
        })
      };
    }

    return Promise.resolve({
      accessToken: { payload: { exp: 0 } },
      getIdToken: () => ({
        payload: { sub: Auth.harUserId, aud: '' },
        getJwtToken: () => 'dummy-token',
        getExpiration: () => 0
      })
    });
  },

  configure: (...args: Parameters<typeof Amplify.configure>) => {
    if (!Auth.isInHARMode) return Amplify.configure(...args);
    return {};
  }
};

export default Auth;
